import { useMemo, useState } from 'react';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { classNames } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import MultiSelect from '@/components/ui/MultiSelect';
import {
  Plus, Ticket, Pencil, Trash2, Power, RotateCcw, Loader2, Search, Info, X,
} from 'lucide-react';

interface StayRange { from: string; to: string }

interface Coupon {
  id: string;
  code: string;
  active: boolean;
  type: 'percent' | 'fixed';
  value: number;
  appliesTo?: string;
  redeemableFrom?: string;
  redeemableUntil?: string;
  staysBetween?: StayRange[];
  minNights?: number;
  minTotal?: number;
  propertyIds?: string[];
  excludePropertyIds?: string[];
  maxRedemptions?: number;
  redemptionCount?: number;
  maxPerEmail?: number;
  redeemedEmails?: string[];
  label?: string;
  createdAt?: string;
  updatedAt?: string;
}

const APPLIES_TO_LABELS: Record<string, string> = {
  lodging: 'Solo alojamiento',
  lodging_plus_cleaning: 'Alojamiento + limpieza',
  total: 'Total',
};

const REJECT_REASONS: { reason: string; cause: string }[] = [
  { reason: 'not_found', cause: 'El código no existe.' },
  { reason: 'inactive', cause: 'El cupón está desactivado (active: false).' },
  { reason: 'not_started', cause: 'Hoy es anterior a la fecha de inicio de canje.' },
  { reason: 'expired', cause: 'Hoy es posterior a la fecha de fin de canje.' },
  { reason: 'dates_not_eligible', cause: 'La estancia no cae dentro de los rangos permitidos.' },
  { reason: 'min_nights', cause: 'La estancia tiene menos noches que el mínimo.' },
  { reason: 'min_total', cause: 'El importe es inferior al mínimo exigido.' },
  { reason: 'property_not_eligible', cause: 'La propiedad está excluida o fuera de la lista permitida.' },
  { reason: 'redemptions_exhausted', cause: 'Se ha alcanzado el límite global de usos.' },
  { reason: 'email_limit', cause: 'El email superó su número máximo de usos.' },
  { reason: 'config_error', cause: 'Cupón mal configurado (p.ej. valor <= 0).' },
];

function formatValue(c: Coupon): string {
  return c.type === 'percent' ? `${c.value}%` : `${c.value}€`;
}

function formatWindow(c: Coupon): string {
  if (!c.redeemableFrom && !c.redeemableUntil) return 'Sin límite';
  return `${c.redeemableFrom || '…'} → ${c.redeemableUntil || '…'}`;
}

export default function CouponsPage() {
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Coupon | null>(null);
  const [showReasons, setShowReasons] = useState(false);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const { data: coupons, loading, refresh } = useApi<Coupon[]>(() => api.getCoupons(), []);
  const { data: properties } = useApi<any[]>(() => api.getProperties(), []);

  const propertyOptions = useMemo(() => {
    const opts = (properties || [])
      .filter((p) => p.hostifyId)
      .map((p) => ({ value: String(p.hostifyId), label: `${p.nickname || p.name || 'Propiedad'} (${p.hostifyId})` }));
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [properties]);

  const filtered = useMemo(() => {
    let list = coupons || [];
    if (activeFilter !== 'all') list = list.filter((c) => (activeFilter === 'active' ? c.active : !c.active));
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((c) => c.code.toLowerCase().includes(q) || (c.label || '').toLowerCase().includes(q));
    return list;
  }, [coupons, activeFilter, search]);

  const openCreate = () => { setEditing(null); setShowForm(true); };
  const openEdit = (c: Coupon) => { setEditing(c); setShowForm(true); };

  const handleSubmit = async (data: any) => {
    if (editing) await api.updateCoupon(editing.code, data);
    else await api.createCoupon(data);
    setShowForm(false);
    setEditing(null);
    refresh();
  };

  const handleToggle = async (c: Coupon) => {
    setBusyCode(c.code);
    try { await api.setCouponActive(c.code, !c.active); refresh(); }
    finally { setBusyCode(null); }
  };

  const handleDelete = async (c: Coupon) => {
    if (!confirm(`¿Eliminar el cupón ${c.code}? Recomendamos desactivar en vez de borrar para conservar el histórico.`)) return;
    setBusyCode(c.code);
    try { await api.deleteCoupon(c.code); refresh(); }
    finally { setBusyCode(null); }
  };

  const handleReset = async (c: Coupon) => {
    if (!confirm(`¿Reiniciar los usos del cupón ${c.code}? Pondrá redemptionCount a 0 y vaciará los emails canjeados.`)) return;
    setBusyCode(c.code);
    try { await api.resetCouponRedemptions(c.code); refresh(); }
    finally { setBusyCode(null); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-2">
          <Ticket size={24} className="text-sereno-dark" />
          <h1 className="text-xl sm:text-2xl font-bold">Cupones</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReasons(true)} className="btn-ghost flex items-center gap-2 text-sm" title="Motivos de rechazo">
            <Info size={16} /> <span className="hidden sm:inline">Motivos</span>
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2 text-sm sm:text-base">
            <Plus size={18} /> <span className="hidden sm:inline">Nuevo cupón</span><span className="sm:hidden">Nuevo</span>
          </button>
        </div>
      </div>

      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/40" />
            <input
              className="input pl-9"
              placeholder="Buscar por código o etiqueta…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="input w-auto" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value as any)}>
            <option value="all">Todos</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          title="No hay cupones"
          description="Crea tu primer cupón de descuento"
          action={<button onClick={openCreate} className="btn-primary"><Plus size={16} className="inline mr-1" /> Nuevo cupón</button>}
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="card overflow-x-auto hidden md:block p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-sereno-dark/50 border-b border-gray-100">
                  <th className="px-4 py-3 font-medium">Código</th>
                  <th className="px-4 py-3 font-medium">Descuento</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Ventana de canje</th>
                  <th className="px-4 py-3 font-medium">Usos</th>
                  <th className="px-4 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} className="border-b border-gray-50 last:border-0 hover:bg-sereno-blue/10">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-sereno-dark">{c.code}</div>
                      {c.label && <div className="text-xs text-sereno-dark/40">{c.label}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-medium">{formatValue(c)}</span>
                      <div className="text-xs text-sereno-dark/40">{APPLIES_TO_LABELS[c.appliesTo || 'lodging_plus_cleaning']}</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={classNames('badge', c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                        {c.active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sereno-dark/60">{formatWindow(c)}</td>
                    <td className="px-4 py-3 text-sereno-dark/60">
                      {c.redemptionCount ?? 0}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {busyCode === c.code ? (
                          <Loader2 size={16} className="animate-spin text-sereno-dark/40" />
                        ) : (
                          <>
                            <button onClick={() => handleToggle(c)} title={c.active ? 'Desactivar' : 'Activar'} className="p-1.5 rounded-lg hover:bg-gray-100">
                              <Power size={15} className={c.active ? 'text-green-600' : 'text-gray-400'} />
                            </button>
                            <button onClick={() => openEdit(c)} title="Editar" className="p-1.5 rounded-lg hover:bg-gray-100">
                              <Pencil size={15} className="text-sereno-dark/60" />
                            </button>
                            <button onClick={() => handleReset(c)} title="Reiniciar usos" className="p-1.5 rounded-lg hover:bg-gray-100">
                              <RotateCcw size={15} className="text-amber-600" />
                            </button>
                            <button onClick={() => handleDelete(c)} title="Eliminar" className="p-1.5 rounded-lg hover:bg-red-50">
                              <Trash2 size={15} className="text-red-500" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-3 md:hidden">
            {filtered.map((c) => (
              <div key={c.id} className="card">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sereno-dark">{c.code}</div>
                    {c.label && <div className="text-xs text-sereno-dark/40">{c.label}</div>}
                  </div>
                  <span className={classNames('badge', c.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500')}>
                    {c.active ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-sereno-dark/60">
                  <div><span className="font-medium text-sereno-dark">{formatValue(c)}</span> · {APPLIES_TO_LABELS[c.appliesTo || 'lodging_plus_cleaning']}</div>
                  <div>Usos: {c.redemptionCount ?? 0}{c.maxRedemptions ? ` / ${c.maxRedemptions}` : ''}</div>
                  <div className="col-span-2">Canje: {formatWindow(c)}</div>
                </div>
                <div className="mt-3 flex items-center gap-1 border-t border-gray-100 pt-2">
                  {busyCode === c.code ? (
                    <Loader2 size={16} className="animate-spin text-sereno-dark/40" />
                  ) : (
                    <>
                      <button onClick={() => handleToggle(c)} className="p-1.5 rounded-lg hover:bg-gray-100"><Power size={15} className={c.active ? 'text-green-600' : 'text-gray-400'} /></button>
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-gray-100"><Pencil size={15} className="text-sereno-dark/60" /></button>
                      <button onClick={() => handleReset(c)} className="p-1.5 rounded-lg hover:bg-gray-100"><RotateCcw size={15} className="text-amber-600" /></button>
                      <button onClick={() => handleDelete(c)} className="p-1.5 rounded-lg hover:bg-red-50 ml-auto"><Trash2 size={15} className="text-red-500" /></button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <Modal isOpen={showForm} onClose={() => { setShowForm(false); setEditing(null); }} title={editing ? `Editar cupón ${editing.code}` : 'Nuevo cupón'} size="lg">
        <CouponForm
          coupon={editing}
          propertyOptions={propertyOptions}
          onSubmit={handleSubmit}
          onCancel={() => { setShowForm(false); setEditing(null); }}
        />
      </Modal>

      <Modal isOpen={showReasons} onClose={() => setShowReasons(false)} title="Motivos de rechazo de la web" size="md">
        <p className="text-sm text-sereno-dark/60 mb-4">
          La web pública devuelve estos motivos cuando un cupón no aplica. Úsalos para entender por qué un cliente no puede canjearlo.
        </p>
        <div className="space-y-2">
          {REJECT_REASONS.map((r) => (
            <div key={r.reason} className="flex gap-3 text-sm">
              <code className="font-mono text-xs bg-gray-100 text-sereno-dark rounded px-2 py-1 h-fit whitespace-nowrap">{r.reason}</code>
              <span className="text-sereno-dark/70">{r.cause}</span>
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

interface CouponFormProps {
  coupon: Coupon | null;
  propertyOptions: { value: string; label: string }[];
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
}

function CouponForm({ coupon, propertyOptions, onSubmit, onCancel }: CouponFormProps) {
  const isEdit = !!coupon;
  const [form, setForm] = useState({
    code: coupon?.code || '',
    active: coupon?.active ?? true,
    type: coupon?.type || 'percent',
    value: coupon?.value != null ? String(coupon.value) : '',
    appliesTo: coupon?.appliesTo || 'lodging_plus_cleaning',
    redeemableFrom: coupon?.redeemableFrom || '',
    redeemableUntil: coupon?.redeemableUntil || '',
    minNights: coupon?.minNights != null ? String(coupon.minNights) : '',
    minTotal: coupon?.minTotal != null ? String(coupon.minTotal) : '',
    maxRedemptions: coupon?.maxRedemptions != null ? String(coupon.maxRedemptions) : '',
    maxPerEmail: coupon?.maxPerEmail != null ? String(coupon.maxPerEmail) : '',
    label: coupon?.label || '',
  });
  const [stays, setStays] = useState<StayRange[]>(coupon?.staysBetween?.length ? coupon.staysBetween : []);
  const [propertyIds, setPropertyIds] = useState<string[]>(coupon?.propertyIds || []);
  const [excludePropertyIds, setExcludePropertyIds] = useState<string[]>(coupon?.excludePropertyIds || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const addStay = () => setStays((s) => [...s, { from: '', to: '' }]);
  const updateStay = (i: number, patch: Partial<StayRange>) =>
    setStays((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeStay = (i: number) => setStays((s) => s.filter((_, idx) => idx !== i));

  const validate = (): string | null => {
    const code = form.code.trim().toUpperCase();
    if (!code) return 'El código es obligatorio';
    if (!/^[A-Z0-9_-]+$/.test(code)) return 'El código solo admite A-Z, 0-9, guion y guion bajo';
    const value = Number(form.value);
    if (!Number.isFinite(value) || value <= 0) return 'El valor debe ser mayor que 0';
    if (form.type === 'percent' && value > 100) return 'Un porcentaje no puede superar 100';
    if (form.redeemableFrom && form.redeemableUntil && form.redeemableFrom > form.redeemableUntil)
      return 'La fecha de inicio de canje debe ser anterior a la de fin';
    for (const r of stays) {
      if ((r.from && !r.to) || (!r.from && r.to)) return 'Completa ambas fechas en cada rango de estancia';
      if (r.from && r.to && r.from > r.to) return 'En los rangos de estancia, "desde" debe ser anterior a "hasta"';
    }
    if (form.minNights && Number(form.minNights) < 0) return 'Las noches mínimas no pueden ser negativas';
    if (form.minTotal && Number(form.minTotal) < 0) return 'El importe mínimo no puede ser negativo';
    const overlap = propertyIds.filter((id) => excludePropertyIds.includes(id));
    if (overlap.length) return 'Una propiedad no puede estar en permitidas y excluidas a la vez';
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    setError(null);
    try {
      await onSubmit({
        code: form.code.trim().toUpperCase(),
        active: form.active,
        type: form.type,
        value: Number(form.value),
        appliesTo: form.appliesTo,
        redeemableFrom: form.redeemableFrom || undefined,
        redeemableUntil: form.redeemableUntil || undefined,
        staysBetween: stays.filter((r) => r.from && r.to),
        minNights: form.minNights !== '' ? Number(form.minNights) : undefined,
        minTotal: form.minTotal !== '' ? Number(form.minTotal) : undefined,
        propertyIds,
        excludePropertyIds,
        maxRedemptions: form.maxRedemptions !== '' ? Number(form.maxRedemptions) : undefined,
        maxPerEmail: form.maxPerEmail !== '' ? Number(form.maxPerEmail) : undefined,
        label: form.label || undefined,
      });
    } catch (e: any) {
      setError(e?.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{error}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Código *</label>
          <input
            className="input font-mono uppercase disabled:bg-gray-50 disabled:text-sereno-dark/50"
            value={form.code}
            disabled={isEdit}
            onChange={(e) => set({ code: e.target.value.toUpperCase() })}
            placeholder="SERENO2026"
          />
          {isEdit && <p className="text-xs text-sereno-dark/40 mt-1">El código no se puede cambiar.</p>}
        </div>
        <div>
          <label className="label">Etiqueta interna</label>
          <input className="input" value={form.label} onChange={(e) => set({ label: e.target.value })} placeholder="Descuento de bienvenida 5%" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Tipo *</label>
          <select className="input" value={form.type} onChange={(e) => set({ type: e.target.value as any })}>
            <option value="percent">Porcentaje (%)</option>
            <option value="fixed">Importe fijo (€)</option>
          </select>
        </div>
        <div>
          <label className="label">Valor *</label>
          <input className="input" type="number" min="0" step="0.01" value={form.value} onChange={(e) => set({ value: e.target.value })} placeholder={form.type === 'percent' ? '5' : '30'} />
        </div>
        <div>
          <label className="label">Aplica sobre</label>
          <select className="input" value={form.appliesTo} onChange={(e) => set({ appliesTo: e.target.value })}>
            <option value="lodging_plus_cleaning">Alojamiento + limpieza</option>
            <option value="lodging">Solo alojamiento</option>
            <option value="total">Total</option>
          </select>
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" checked={form.active} onChange={(e) => set({ active: e.target.checked })} className="w-4 h-4 rounded" />
        <span className="font-medium text-sereno-dark">Cupón activo</span>
      </label>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-sm font-semibold text-sereno-dark mb-3">Ventana de canje (fecha de reserva)</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="label">Desde</label>
            <input className="input" type="date" value={form.redeemableFrom} onChange={(e) => set({ redeemableFrom: e.target.value })} />
          </div>
          <div>
            <label className="label">Hasta</label>
            <input className="input" type="date" value={form.redeemableUntil} onChange={(e) => set({ redeemableUntil: e.target.value })} />
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold text-sereno-dark">Rangos de estancia elegibles</p>
          <button type="button" onClick={addStay} className="btn-ghost text-xs flex items-center gap-1"><Plus size={14} /> Añadir rango</button>
        </div>
        {stays.length === 0 && <p className="text-xs text-sereno-dark/40">Sin restricción: cualquier fecha de estancia.</p>}
        <div className="space-y-2">
          {stays.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className="input" type="date" value={r.from} onChange={(e) => updateStay(i, { from: e.target.value })} />
              <span className="text-sereno-dark/40">→</span>
              <input className="input" type="date" value={r.to} onChange={(e) => updateStay(i, { to: e.target.value })} />
              <button type="button" onClick={() => removeStay(i)} className="p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0"><X size={15} className="text-red-500" /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Noches mínimas</label>
          <input className="input" type="number" min="0" value={form.minNights} onChange={(e) => set({ minNights: e.target.value })} />
        </div>
        <div>
          <label className="label">Importe mínimo (€)</label>
          <input className="input" type="number" min="0" step="0.01" value={form.minTotal} onChange={(e) => set({ minTotal: e.target.value })} />
        </div>
      </div>

      <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <MultiSelect
          label="Propiedades permitidas (allowlist)"
          options={propertyOptions}
          selected={propertyIds}
          onChange={setPropertyIds}
          searchable
          allLabel="Todas"
        />
        <MultiSelect
          label="Propiedades excluidas (denylist)"
          options={propertyOptions}
          selected={excludePropertyIds}
          onChange={setExcludePropertyIds}
          searchable
          allLabel="Ninguna"
        />
      </div>

      <div className="border-t border-gray-100 pt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="label">Usos máximos (global)</label>
          <input className="input" type="number" min="0" value={form.maxRedemptions} onChange={(e) => set({ maxRedemptions: e.target.value })} />
        </div>
        <div>
          <label className="label">Usos máximos por email</label>
          <input className="input" type="number" min="0" value={form.maxPerEmail} onChange={(e) => set({ maxPerEmail: e.target.value })} />
        </div>
      </div>

      {isEdit && (
        <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-sereno-dark/50">
          Usos actuales: <span className="font-medium text-sereno-dark/70">{coupon?.redemptionCount ?? 0}</span>
          {' · '}Emails canjeados: <span className="font-medium text-sereno-dark/70">{coupon?.redeemedEmails?.length ?? 0}</span>
          <br />Estos contadores los gestiona la web y no se modifican al editar.
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <button type="button" onClick={onCancel} className="btn-ghost">Cancelar</button>
        <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2">
          {saving && <Loader2 size={16} className="animate-spin" />}
          {saving ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear cupón'}
        </button>
      </div>
    </form>
  );
}
